<script setup lang="ts">
import type { RuneBlessRes, RuneItemType } from '@/types/rune'
import { getRuneName } from '@/core/rune'
import RuneInfo from '../../item/components/RuneInfo.vue'
import { ref, computed, watch } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import { useCommonStore } from '@/stores/modules/common'
import { socketEmitAsync } from '@/utils/web-socket'
import { dayjs } from 'element-plus'
import { QuestionFilled } from '@element-plus/icons-vue'
import { formatNumberWithWan } from '@/utils'
import { RuneRoleText } from '@/views/item/data'
import { soulClass } from '../data'

const commonStore = useCommonStore()
const userStore = useUserStore()

const freeBless = computed(() => dayjs().diff(dayjs(userStore.runeBlessResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day') > 0)
const choiceSoulType = ref(0)
const receiveShow = ref(false)
const receiveRuneList = ref<RuneItemType[]>()
function handleBless(type: 'free' | 'gold' | 'diamond', count: number = 1) {
  if (userStore.runeList.length > userStore.getRuneCapacity) return ElMessage.warning('符石背包已满，无法进行祈福')

  let priceMsg = ''
  const goldPrice = count * 1000000
  if (type === 'gold') {
    if (userStore.gold < goldPrice) return ElMessage.warning('金币不足')
    priceMsg = `（本次祈福花费<span class="${type}">${goldPrice} 金币</span>）`
  } else if (type === 'diamond') {
    if (userStore.consumables.runeBlessCoupon < count) {
      ElMessageBox.confirm(`<p class="affix"><span class="diamond">高级祈福券</span>不足，是否前往购买？</p>`, '提示', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
        appendTo: 'body',
        confirmButtonClass: 'is-plain',
      })
        .then(() => {
          commonStore.path = 'Mall'
        })
        .catch(() => {})
      return
    }
    priceMsg = `（本次祈福花费<span class="${type}">${count} 高级祈福券</span>）`
  }
  ElMessageBox.confirm(
    `<p class="affix">确定进行${
      type === 'diamond' ? `【<span class="${soulClass[choiceSoulType.value]}">${RuneRoleText[choiceSoulType.value]}倾向</span>】的` : ''
    }祈福吗？${priceMsg}</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<RuneBlessRes>({ type: 'runeBless', data: { type, count, choiceSoulType: choiceSoulType.value } })
      userStore.runeList.push(...res.runeList)
      userStore.runeBlessTimes = res.runeBlessTimes
      if (type === 'free') {
        userStore.runeBlessResetTime = dayjs().format('YYYY-MM-DD HH:mm:ss')
      }
      receiveShow.value = true
      receiveRuneList.value = res.runeList
      if (type === 'gold') {
        userStore.gold -= goldPrice
      } else if (type === 'diamond') {
        userStore.consumables.runeBlessCoupon -= count
        userStore.consumables.runeStar += count
      }
    })
    .catch(() => {})
}

watch(
  () => userStore.soulType,
  (val) => {
    choiceSoulType.value = val
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <div class="rune-bless">
    <p style="text-align: right; font-size: 14px; margin: 5px 0">
      💰 {{ formatNumberWithWan(userStore.gold) }} 🎫 {{ userStore.consumables.runeBlessCoupon }} ✨ {{ userStore.consumables.runeStar }}
    </p>
    <div class="bless-wrap border-wrap">
      <h4>
        殿堂祈福
        <div class="help-info">
          <el-popover placement="bottom" width="100%" trigger="click">
            <template #reference>
              <el-icon size="16"><QuestionFilled /></el-icon>
            </template>
            <div class="affix">
              <h4>祈福说明</h4>
              <p>1. 在觉醒殿堂中祈福您可以获得<span class="awaken">符石</span>奖励。</p>
              <p>
                2. 获得符石时，<span class="info">70%</span>概率为所属战魂的符石（<span class="diamond">高级祈福</span
                >可选择抽取倾向），携带所属战魂的符石可获得 <span class="grow">20%</span>的属性加成。
              </p>
              <p>3. 同名符石最多同时装备 <span class="info">2</span>块。</p>
              <p>4. 每日可进行<span class="grow">免费祈福</span>，可获得符石 <span class="info">2</span>块，会员为 <span class="info">3</span>块。</p>
              <p>
                4. <span class="gold">普通祈福</span>时的最高产出<span class="epic">史诗</span>符石，概率为：<span class="normal">普通</span>
                83%，<span class="race">稀有</span> 15%，<span class="epic">史诗</span> 2%。
              </p>
              <p>
                5.
                <span class="diamond">高级祈福</span>时最高产出<span class="legend">传说</span>符石，同时在祈福后会获得 1<span class="awaken"
                  >符石碎星</span
                >，概率为：<span class="race">稀有</span> 79%，<span class="epic">史诗</span> 18%，<span class="legend">传说</span> 3%。
              </p>
              <p>6. 符石的评分上下限受品质影响，如表格所示：</p>
              <table border style="text-align: center; width: 100%">
                <tr>
                  <td>符石评分</td>
                  <td class="normal">普通</td>
                  <td class="race">稀有</td>
                  <td class="epic">史诗</td>
                  <td class="legend">传说</td>
                </tr>
                <tr>
                  <td>最低值</td>
                  <td>4</td>
                  <td>6</td>
                  <td>8</td>
                  <td>10</td>
                </tr>
                <tr>
                  <td>最高值</td>
                  <td>8</td>
                  <td>10</td>
                  <td>14</td>
                  <td>20</td>
                </tr>
              </table>
            </div>
          </el-popover>
        </div>
      </h4>

      <div class="bless-list">
        <div class="left bless-item">
          <h5 class="gold">普通祈福</h5>
          <p>1,000,000 金币 / 次</p>
          <p>距离保底次数：{{ 10 - userStore.runeBlessTimes.gold }}</p>
          <el-button type="success" plain @click="handleBless('free')" :disabled="!freeBless">{{ freeBless ? '免费祈福' : '静候明日' }}</el-button>
          <el-button type="primary" plain @click="handleBless('gold', 1)">祈福 1次</el-button>
          <el-button type="primary" plain @click="handleBless('gold', 10)">祈福 10次</el-button>
        </div>
        <div class="right bless-item">
          <h5 class="diamond">高级祈福</h5>
          <p>1 高级祈福券 / 次</p>
          <p>距离保底次数：{{ 10 - userStore.runeBlessTimes.diamond }}</p>
          <p style="display: flex; align-items: center; margin-bottom: 5px">
            <span style="margin-right: 4px">抽取倾向</span>
            <el-select size="small" v-model="choiceSoulType" :class="soulClass[choiceSoulType]" style="width: 90px">
              <el-option v-for="item of [1, 2, 3]" :key="item" :label="RuneRoleText[item]" :value="item">
                <span :class="soulClass[item]">{{ RuneRoleText[item] }}</span>
              </el-option>
            </el-select>
          </p>
          <el-button type="primary" plain @click="handleBless('diamond', 1)">祈福 1次</el-button>
          <el-button type="primary" plain @click="handleBless('diamond', 10)">祈福 10次</el-button>
        </div>
      </div>
      <p style="font-weight: bold; font-size: 14px; margin: 10px 0; text-align: center; color: red">* 请留意符石数值将持续平衡（可能削弱/增强）</p>
    </div>

    <el-dialog v-model="receiveShow" width="100%" style="max-width: 500px" destroy-on-close>
      <h2>恭喜获得</h2>

      <div class="btn-wrap" style="max-height: 50vh; overflow: auto">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of receiveRuneList" :key="item.id">
            <el-popover placement="bottom" width="300px" trigger="click">
              <template #reference>
                <CommonBtn size="small" style="margin-bottom: 5px">
                  <span :class="item.quality">
                    {{ getRuneName(item.runeId) }}
                  </span>
                </CommonBtn>
              </template>
              <RuneInfo :rune-info="item" :show-action="false" />
            </el-popover>
          </el-col>
        </el-row>
      </div>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="receiveShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.rune-bless {
  margin-bottom: 10px;
  flex-flow: column nowrap;
  h4 {
    display: flex;
    justify-content: center;
    position: relative;
    .help-info {
      position: absolute;
      margin-left: 10px;
      right: 30%;
    }
  }
  .bless-list {
    display: flex;
    justify-content: space-between;
    .bless-item {
      display: flex;
      flex-flow: column nowrap;
      width: 50%;
      align-items: center;
      h5 {
        font-size: 15px;
        margin: 10px 0;
      }
      p {
        font-size: 14px;
        margin: 5px 0;
      }
      .el-button {
        margin-left: 0;
        margin-bottom: 5px;
      }
    }
  }
  .btn-wrap > div {
    align-items: flex-start;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }

  :deep() {
    .war-god {
      .el-select__selected-item {
        span {
          color: var(--el-color-danger);
        }
      }
    }
    .wisdom-god {
      .el-select__selected-item {
        span {
          color: var(--el-color-primary);
        }
      }
    }
    .hunt-god {
      .el-select__selected-item {
        span {
          color: var(--el-color-success);
        }
      }
    }
  }
}
</style>
